import { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";
import { useNavigation } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import { useQueryClient } from "@tanstack/react-query";
import { spacing } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { supabase } from "../lib/supabase";
import { useAuth } from "../hooks/useAuth";
import { useDispatch } from "react-redux";
import { signOutRequest } from "../store/slices/authSlice";
import * as SecureStore from "expo-secure-store";
import { navigationRef } from "../services/NavigationService";
import { platformAlert } from "../utils/platformAlert";

export default function DeleteAccountScreen() {
  const navigation = useNavigation();
  const colors = useColors();
  const styles = createStyles(colors);
  const { session } = useAuth();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const insets = useSafeAreaInsets();

  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmitRequest = async () => {
    if (!session?.user?.id) {
      platformAlert("Error", "You must be logged in to delete your account");
      return;
    }

    // Validate required fields
    if (!reason.trim()) {
      platformAlert("Required Field", "Please select a reason for deletion");
      return;
    }

    if (confirmationText.toLowerCase() !== "delete my account") {
      platformAlert(
        "Confirmation Required",
        "Please type 'DELETE MY ACCOUNT' exactly to confirm"
      );
      return;
    }

    platformAlert(
      "Confirm Account Deletion",
      "Are you absolutely sure? This action cannot be undone. Your profile and personal data will be permanently removed (limited transaction records retained for legal/accounting reasons).",
      [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Delete Now",
          style: "destructive",
          onPress: submitDeletionRequest,
        },
      ]
    );
  };

  const submitDeletionRequest = async () => {
    try {
      setIsSubmitting(true);
      const userId = session!.user!.id;

      console.log(
        `[DeleteAccount] Starting account deletion for user: ${userId}`
      );

      // Get the current session token for the edge function
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      if (!currentSession?.access_token) {
        throw new Error("No valid session found");
      }

      // Call the delete-account edge function
      const { data, error } = await supabase.functions.invoke(
        "delete-account",
        {
          body: {
            reason: reason,
            feedback: feedback.trim() || null,
          },
          headers: {
            Authorization: `Bearer ${currentSession.access_token}`,
          },
        }
      );

      if (error) {
        console.error("Edge function error:", error);
        throw new Error(error.message || "Failed to delete account");
      }

      if (!data?.success) {
        console.error("Edge function returned failure:", data);
        throw new Error(data?.error || "Account deletion failed");
      }

      console.log("[DeleteAccount] Edge function completed:", data);

      // Sign out the user globally and clean up realtime channels
      try { supabase.removeAllChannels?.(); } catch {}
      await supabase.auth.signOut({ scope: "global" });

      // Clear React Query cache to prevent stale vendor data from showing
      // This is critical - without this, old vendor data persists after re-signup
      queryClient.clear();

      // Clear all storage keys
      const authKeys = [
        "supabase.auth.token",
        "supabase.auth.refreshToken",
        "supabase.auth.user",
        "supabase.auth.expires_at",
        "supabase.auth.provider_token",
        "supabase.auth.provider_refresh_token",
      ];

      for (const key of authKeys) {
        await SecureStore.deleteItemAsync(key).catch(() => {});
      }

      dispatch(signOutRequest());

      // Show success message with deletion summary from edge function
      const summary = data.summary;
      const successMessage = `Account successfully deleted.`;

      // Navigate immediately on web - don't wait for Alert callback
      // On web, Alert might not work the same way as native
      if (Platform.OS === 'web') {
        // Wait a bit for auth state to update, then navigate
        setTimeout(() => {
          if (navigationRef.isReady()) {
            console.log('[DeleteAccount] Navigating to AuthFlow on web');
            navigationRef.reset({
              index: 0,
              routes: [{ name: 'AuthFlow' as never }],
            });
          } else {
            console.warn('[DeleteAccount] Navigation ref not ready, retrying...');
            // Retry after a longer delay
            setTimeout(() => {
              if (navigationRef.isReady()) {
                navigationRef.reset({
                  index: 0,
                  routes: [{ name: 'AuthFlow' as never }],
                });
              }
            }, 500);
          }
        }, 100);
      }

      // Show alert - on native, navigation happens in callback
      // On web, navigation already happened above
      platformAlert("Account Deleted", successMessage, [
        {
          text: "OK",
          onPress: () => {
            // On native platforms, navigate here
            if (Platform.OS !== 'web') {
              navigation.reset({
                index: 0,
                routes: [{ name: "AuthFlow" as never }],
              });
            }
          },
        },
      ]);
    } catch (error: any) {
      console.error("Error deleting account:", error);
      platformAlert(
        "Deletion Error",
        error?.message ||
          "Failed to delete account. Please try again or contact support."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const reasons = [
    "I no longer use the app",
    "Privacy concerns",
    "Too many notifications",
    "Found a better alternative",
    "Account security concerns",
    "Difficulty using the app",
    "Other",
  ];

  return (
    <SafeAreaView style={styles.container} edges={["bottom", "top"]}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Feather name="arrow-left" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Delete Account</Text>
      </View>

      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        // Offset accounts for the header + safe area top
        keyboardVerticalOffset={insets.top + 56}
      >
        <ScrollView
          style={styles.content}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
          // Give extra bottom space so the "DELETE MY ACCOUNT" field stays above the keyboard
          contentContainerStyle={{ paddingBottom: insets.bottom + spacing.xl * 3 }}
          automaticallyAdjustKeyboardInsets
        >
          <View style={styles.warningContainer}>
            <Feather name="alert-triangle" size={24} color={colors.error} />
            <Text style={styles.warningText}>
              This action is permanent and cannot be undone
            </Text>
          </View>

          <View style={styles.infoContainer}>
            <Text style={styles.infoTitle}>
              What happens when you delete your account?
            </Text>
            <View style={styles.infoList}>
              <Text style={styles.infoItem}>
                • Your profile and personal data will be removed from the app.
              </Text>
              <Text style={styles.infoItem}>
                • Transaction records (orders, payments, refunds, disputes, and required audit logs) will be retained for legal/accounting and fraud-prevention reasons and will not be shown in your account.
              </Text>
              <Text style={styles.infoItem}>
                • Your vendor store (if any) will be deactivated and products unpublished; items tied to past orders remain archived.
              </Text>
              <Text style={styles.infoItem}>
                • You will be signed out and lose access immediately; background cleanup (e.g., media) may continue briefly.
              </Text>
              <Text style={styles.infoItem}>
                • This action is permanent and cannot be undone.
              </Text>
            </View>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionLabel}>
              Why are you deleting your account? *
            </Text>
            {reasons.map((reasonOption, index) => (
              <TouchableOpacity
                key={index}
                style={styles.reasonOption}
                onPress={() => setReason(reasonOption)}
              >
                <View
                  style={[
                    styles.radioButton,
                    reason === reasonOption && styles.radioButtonSelected,
                  ]}
                >
                  {reason === reasonOption && (
                    <View style={styles.radioButtonInner} />
                  )}
                </View>
                <Text style={styles.reasonText}>{reasonOption}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionLabel}>
              Additional feedback (optional)
            </Text>
            <Text style={styles.hint}>
              Help us improve by telling us more about your experience
            </Text>
            <TextInput
              style={styles.textArea}
              value={feedback}
              onChangeText={setFeedback}
              placeholder="Share your thoughts..."
              multiline
              numberOfLines={4}
              textAlignVertical="top"
              maxLength={500}
            />
            <Text style={styles.characterCount}>{feedback.length}/500</Text>
          </View>

          <View style={styles.section}>
            <Text style={styles.sectionLabel}>
              Type "DELETE MY ACCOUNT" to confirm *
            </Text>
            <TextInput
              style={[
                styles.input,
                confirmationText.toLowerCase() === "delete my account"
                  ? styles.inputValid
                  : styles.inputInvalid,
              ]}
              value={confirmationText}
              onChangeText={setConfirmationText}
              placeholder="DELETE MY ACCOUNT"
              autoCapitalize="characters"
              returnKeyType="done"
              blurOnSubmit={true}
            />
          </View>

          <TouchableOpacity
            style={[
              styles.deleteButton,
              (!reason.trim() ||
                confirmationText.toLowerCase() !== "delete my account" ||
                isSubmitting) &&
                styles.disabledButton,
            ]}
            onPress={handleSubmitRequest}
            disabled={
              !reason.trim() ||
              confirmationText.toLowerCase() !== "delete my account" ||
              isSubmitting
            }
          >
            {isSubmitting ? (
              <ActivityIndicator size="small" color={colors.surface} />
            ) : (
              <Text style={styles.deleteButtonText}>Delete Account Now</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => navigation.goBack()}
            disabled={isSubmitting}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      padding: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    backButton: {
      marginRight: spacing.md,
    },
    headerTitle: {
      fontSize: 18,
      fontWeight: "600",
      color: colors.text,
    },
    content: {
      flex: 1,
      padding: spacing.md,
    },
    warningContainer: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: colors.backgroundSecondary,
      padding: spacing.md,
      borderRadius: 8,
      marginBottom: spacing.lg,
      gap: spacing.sm,
    },
    warningText: {
      flex: 1,
      fontSize: 16,
      fontWeight: "500",
      color: colors.error,
    },
    infoContainer: {
      backgroundColor: colors.backgroundSecondary,
      padding: spacing.md,
      borderRadius: 8,
      marginBottom: spacing.lg,
    },
    infoTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    infoList: {
      gap: spacing.xs,
    },
    infoItem: {
      fontSize: 14,
      color: colors.textLight,
      lineHeight: 20,
    },
    section: {
      marginBottom: spacing.lg,
    },
    sectionLabel: {
      fontSize: 16,
      fontWeight: "500",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    hint: {
      fontSize: 14,
      color: colors.textLight,
      marginBottom: spacing.sm,
    },
    reasonOption: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: spacing.sm,
      gap: spacing.sm,
    },
    radioButton: {
      width: 20,
      height: 20,
      borderRadius: 10,
      borderWidth: 2,
      borderColor: colors.border,
      alignItems: "center",
      justifyContent: "center",
    },
    radioButtonSelected: {
      borderColor: colors.primary,
    },
    radioButtonInner: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: colors.primary,
    },
    reasonText: {
      flex: 1,
      fontSize: 16,
      color: colors.text,
    },
    textArea: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.md,
      fontSize: 16,
      backgroundColor: colors.surface,
      color: colors.text,
      minHeight: 100,
    },
    characterCount: {
      fontSize: 12,
      color: colors.textLight,
      textAlign: "right",
      marginTop: spacing.xs,
    },
    input: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.md,
      fontSize: 16,
      backgroundColor: colors.surface,
      color: colors.text,
    },
    inputValid: {
      borderColor: colors.success || colors.primary,
    },
    inputInvalid: {
      borderColor: colors.error,
    },
    deleteButton: {
      backgroundColor: colors.error,
      padding: spacing.md,
      borderRadius: 8,
      alignItems: "center",
      marginTop: spacing.lg,
    },
    disabledButton: {
      opacity: 0.5,
    },
    deleteButtonText: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.surface,
    },
    cancelButton: {
      padding: spacing.md,
      alignItems: "center",
      marginTop: spacing.sm,
    },
    cancelButtonText: {
      fontSize: 16,
      fontWeight: "500",
      color: colors.textLight,
    },
  });
